'use client';

import { Skeleton } from '@common/components';
import { cn } from '@core/utils';
import { useParams } from 'next/navigation';
import { useSyncExternalStore } from 'react';

import { buildGiftHeroImageUrl } from '@/app/_components/GiftCard/giftImage';
import {
  getGiftPreviewSnapshot,
  matchGiftPreview,
  subscribeGiftPreview,
} from '@/app/_components/GiftCard/giftPreview';

import { DELIVERY_PERIOD_LABEL } from '../constants';
import { PriceInfo } from './GiftDetailHeader/_components/PriceInfo';
import { ThumbnailBackedImage } from './GiftDetailHeader/_components/ThumbnailBackedImage';

const DESKTOP_THUMBNAIL_SLOT_COUNT = 6;

//* loading.tsx 안에 client 컴포넌트를 두면 Next의 scroll-to-top이 죽어(실측) 로딩 경계 밖 layout에서 그린다
//* 래퍼·간격·타이포는 GiftDetailTemplate·HeaderInfoSection 마크업을 폭별로 그대로 따른다 — 모르는 값만 같은 줄높이의 스켈레톤으로 채워야 본문 도착 시 시프트가 0이다
export const GiftEntryPreview = () => {
  const params = useParams<{ productNumber: string }>();
  const tapped = useSyncExternalStore(
    subscribeGiftPreview,
    getGiftPreviewSnapshot,
    () => null,
  );
  const preview = matchGiftPreview(tapped, params?.productNumber);

  if (!preview) {
    return null;
  }

  return (
    <div
      aria-hidden="true"
      className="absolute inset-0 z-[2] overflow-hidden bg-Gray-white"
    >
      <div
        className={cn(
          'mt-20 px-40 content-container',
          'mobile-tablet:mt-0 mobile-tablet:px-0',
        )}
      >
        <div
          className={cn(
            'grid grid-cols-2 gap-28',
            'mobile-tablet:grid-cols-1 mobile-tablet:gap-0',
          )}
        >
          <div>
            <div
              className={cn(
                'relative mb-20 aspect-square w-full overflow-hidden rounded-[48px] bg-Gray-black/10',
                'mobile-tablet:mb-0 mobile-tablet:rounded-none',
              )}
            >
              {preview.mainImageUrl ? (
                <ThumbnailBackedImage
                  key={preview.productNumber}
                  src={buildGiftHeroImageUrl(preview.mainImageUrl)}
                  alt={preview.productName ?? '답례품 이미지'}
                  thumbnailSrc={preview.thumbnailSrc}
                  loading="eager"
                />
              ) : null}
            </div>
            {/* 실제 장수는 본문이 와야 알 수 있어 한 줄(6칸) 높이만 잡는다 */}
            <div className="grid grid-cols-6 gap-10 mobile-tablet:hidden">
              {Array.from({ length: DESKTOP_THUMBNAIL_SLOT_COUNT }).map(
                (_, index) => (
                  <Skeleton
                    key={index}
                    className="aspect-square w-full rounded-large"
                  />
                ),
              )}
            </div>
          </div>

          <section
            className={cn(
              'flex w-full flex-col',
              'mobile-tablet:relative mobile-tablet:z-[1] mobile-tablet:-mt-20',
              'mobile-tablet:rounded-t-large mobile-tablet:bg-Gray-white mobile-tablet:pt-24',
            )}
          >
            <div className="flex flex-col px-24">
              <div className="mb-8 flex items-center justify-between gap-4">
                <p
                  className={cn(
                    'font-400 text-Gray-600 typo-body3',
                    'mobile-tablet:typo-body4',
                  )}
                >
                  {preview.institutionName}
                </p>
                <span className="size-22" />
              </div>
              <div
                className={cn(
                  'mb-8 flex justify-between gap-16',
                  'mobile-tablet:mb-16',
                )}
              >
                <h2
                  className={cn(
                    'flex-1 break-all font-700 text-Gray-900 typo-h4',
                    'mobile-tablet:font-600 mobile-tablet:text-Gray-black mobile-tablet:typo-body1',
                  )}
                >
                  {preview.productName}
                </h2>
              </div>
              <div
                className={cn(
                  'mb-28 flex flex-wrap items-center gap-8',
                  'mobile-tablet:mb-16 mobile-tablet:gap-4',
                )}
              >
                {preview.donationAmount === 100000 ? (
                  <span className="rounded-xsmall bg-G-50 px-12 py-4 font-700 text-G-500 typo-caption1">
                    기부하는 10만원, 모두 세액공제!
                  </span>
                ) : null}
                <Skeleton
                  typo="body3"
                  className="ml-auto w-80 rounded-xxsmall mobile-tablet:typo-body4"
                />
              </div>
              {preview.donationAmount ? (
                <div className="mb-20">
                  <PriceInfo amount={preview.donationAmount} />
                </div>
              ) : null}
            </div>
            <div
              className={cn(
                'flex flex-col gap-24 border-t border-Gray-200 p-20',
                'mobile-tablet:gap-16',
              )}
            >
              <div
                className={cn(
                  'flex flex-col gap-12 typo-body3',
                  'mobile-tablet:typo-body4',
                )}
              >
                <div className="flex items-center gap-16">
                  <Skeleton className="rounded-xxsmall text-transparent">
                    <p className="w-92 font-400">배송정보</p>
                  </Skeleton>
                  <Skeleton className="rounded-xxsmall text-transparent">
                    <p className="font-400">무료배송</p>
                  </Skeleton>
                </div>
                <div className="flex gap-16">
                  <Skeleton className="rounded-xxsmall text-transparent">
                    <p className="w-92 shrink-0 font-400">배송안내</p>
                  </Skeleton>
                  <div className="flex flex-col gap-4">
                    <Skeleton className="rounded-xxsmall text-transparent">
                      <p className="font-400">{DELIVERY_PERIOD_LABEL}</p>
                    </Skeleton>
                    <Skeleton className="rounded-xxsmall text-transparent">
                      <p className="font-500">
                        (식품의 경우 지연 발생 시 1주~2주 소요)
                      </p>
                    </Skeleton>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};
